import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * AvailabilityCalendar — premium range-picker driven by REAL vehicle
 * availability from `GET /vehicles/:id/blocked-ranges`.
 *
 * Desktop  : two months side-by-side
 * Mobile   : single-month view, tap chevrons to move
 * States   : available (default), past, blocked (reservation / maintenance),
 *            in-range (customer's selection), start/end pill.
 *
 * Selection: click a start day, click an end day. Clicking a blocked day
 * inside a would-be range aborts the selection with a soft toast (parent
 * decides). Range that spans a blocked day is impossible — the picker
 * prevents it directly.
 *
 * Props:
 *   vehicleId (string, required)
 *   value  = { start: 'YYYY-MM-DD' | null, end: 'YYYY-MM-DD' | null }
 *   onChange({ start, end })
 *   minDate (Date, default = today)
 */
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const toYmd = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
};
const parseYmd = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const monthLabel = (d) => d.toLocaleString(undefined, { month: "long", year: "numeric" });

function buildMonth(anchor) {
  const y = anchor.getFullYear(), m = anchor.getMonth();
  const first = new Date(y, m, 1);
  const days = new Date(y, m + 1, 0).getDate();
  const grid = [];
  for (let i = 0; i < first.getDay(); i += 1) grid.push(null);
  for (let d = 1; d <= days; d += 1) grid.push(new Date(y, m, d));
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

// Build a Set<'YYYY-MM-DD'> of every day fully or partially blocked.
function buildBlockedSet(ranges) {
  const out = new Set();
  for (const r of ranges || []) {
    if (!r?.start || !r?.end) continue;
    const start = new Date(r.start);
    const end = new Date(r.end);
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= last) {
      out.add(toYmd(cur));
      cur.setDate(cur.getDate() + 1);
    }
  }
  return out;
}

// Given a start & candidate end, return true if any day in [start..end] is blocked
function rangeCrossesBlock(startYmd, endYmd, blockedSet) {
  const s = parseYmd(startYmd);
  const e = parseYmd(endYmd);
  if (!s || !e) return false;
  const cur = new Date(s);
  while (cur <= e) {
    if (blockedSet.has(toYmd(cur))) return true;
    cur.setDate(cur.getDate() + 1);
  }
  return false;
}

// Find next available range of `desiredDays` days starting from a given date.
function findNextAvailableRange(startYmd, desiredDays, blockedSet, maxLookaheadDays = 120) {
  let cur = parseYmd(startYmd) || new Date();
  for (let i = 0; i < maxLookaheadDays; i += 1) {
    const s = toYmd(cur);
    const end = new Date(cur);
    end.setDate(end.getDate() + Math.max(1, desiredDays - 1));
    if (!rangeCrossesBlock(s, toYmd(end), blockedSet)) {
      return { start: s, end: toYmd(end) };
    }
    cur.setDate(cur.getDate() + 1);
  }
  return null;
}

export default function AvailabilityCalendar({ vehicleId, value, onChange, minDate }) {
  const today = useMemo(() => {
    const d = minDate ? new Date(minDate) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [minDate]);

  const [anchor, setAnchor] = useState(() => {
    const start = value?.start ? parseYmd(value.start) : today;
    return new Date(start.getFullYear(), start.getMonth(), 1);
  });
  const [hover, setHover] = useState(null);
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vehicleId) return;
    setLoading(true);
    const start = new Date(anchor);
    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 3, 0);
    api
      .get(`/vehicles/${vehicleId}/blocked-ranges`, {
        params: { start: start.toISOString(), end: end.toISOString() },
      })
      .then((r) => setRanges(r.data?.ranges || []))
      .catch((e) => setError("Couldn't load availability"))
      .finally(() => setLoading(false));
  }, [vehicleId, anchor]);

  const blockedSet = useMemo(() => buildBlockedSet(ranges), [ranges]);
  const nextMonth = useMemo(
    () => new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1),
    [anchor]
  );

  const startYmd = value?.start || null;
  const endYmd = value?.end || null;

  const dayClick = (d) => {
    if (!d) return;
    const ymd = toYmd(d);
    if (d < today) return;
    if (blockedSet.has(ymd)) return;

    if (!startYmd || (startYmd && endYmd)) {
      onChange({ start: ymd, end: null });
      return;
    }
    // second click completes the range
    let s = startYmd, e = ymd;
    if (parseYmd(e) < parseYmd(s)) [s, e] = [e, s];
    if (rangeCrossesBlock(s, e, blockedSet)) {
      // Bad range — auto-suggest the nearest available window of same length
      const desiredLen = Math.max(1,
        Math.round((parseYmd(e) - parseYmd(s)) / 86400000) + 1);
      const suggestion = findNextAvailableRange(s, desiredLen, blockedSet);
      onChange({ start: ymd, end: null, suggestion });
      return;
    }
    onChange({ start: s, end: e });
  };

  const inHover = (d) => {
    if (!d || !startYmd || endYmd) return false;
    const ymd = toYmd(d);
    if (!hover) return false;
    const [a, b] = [startYmd, hover].sort();
    return ymd > a && ymd < b;
  };

  const cellCls = (d) => {
    if (!d) return "invisible";
    const ymd = toYmd(d);
    const isPast = d < today;
    const isBlocked = blockedSet.has(ymd);
    const isStart = startYmd === ymd;
    const isEnd = endYmd === ymd;
    const inRange = startYmd && endYmd && ymd > startYmd && ymd < endYmd;
    const hoverInRange = inHover(d);

    if (isStart || isEnd)
      return "bg-cyan text-black font-semibold ring-1 ring-cyan/70";
    if (inRange || hoverInRange)
      return "bg-cyan/25 text-white ring-1 ring-cyan/40";
    if (isPast)
      return "text-white/25 line-through cursor-not-allowed";
    if (isBlocked)
      return "text-red-300/60 line-through cursor-not-allowed bg-red-500/8";
    return "text-white/85 hover:bg-white/8 hover:ring-1 hover:ring-cyan/30";
  };

  const renderMonth = (m, keyPrefix) => (
    <div className="flex-1 min-w-[280px]">
      <div className="text-center label-caps text-[10px] text-white/60 mb-3">
        {monthLabel(m)}
      </div>
      <div className="grid grid-cols-7 gap-y-1 mb-1 text-[10px] text-white/40 label-caps text-center">
        {DOW.map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {buildMonth(m).map((d, i) => (
          <button
            type="button"
            key={`${keyPrefix}-${i}`}
            data-testid={d ? `avail-cal-day-${toYmd(d)}` : undefined}
            onClick={() => dayClick(d)}
            onMouseEnter={() => d && setHover(toYmd(d))}
            onMouseLeave={() => setHover(null)}
            disabled={!d || d < today || blockedSet.has(d ? toYmd(d) : "")}
            className={`h-9 rounded-lg text-xs transition ${cellCls(d)}`}
          >
            {d ? d.getDate() : ""}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div
      data-testid="availability-calendar"
      className="rounded-2xl border border-white/10 bg-black/30 p-4 sm:p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          data-testid="avail-cal-prev"
          onClick={() => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() - 1, 1))}
          className="p-1.5 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30"
        >
          <ChevronLeft size={14} />
        </button>
        <div className="label-caps text-cyan text-[10px] tracking-widest">
          Availability calendar
        </div>
        <button
          type="button"
          data-testid="avail-cal-next"
          onClick={() => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + 1, 1))}
          className="p-1.5 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30"
        >
          <ChevronRight size={14} />
        </button>
      </div>
      {loading ? (
        <div className="text-center text-white/50 py-6 inline-flex items-center gap-2 justify-center w-full">
          <Loader2 size={14} className="animate-spin" /> Loading availability…
        </div>
      ) : error ? (
        <div className="text-center text-red-300 text-xs py-6">{error}</div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-6">
          {renderMonth(anchor, "a")}
          <div className="hidden sm:block">{renderMonth(nextMonth, "b")}</div>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[10px] text-white/50">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-cyan" /> Selected</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-cyan/25 ring-1 ring-cyan/40" /> In range</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-red-500/20" /> Booked / blocked</span>
      </div>
      {startYmd && !endYmd && (
        <div className="mt-3 text-[11px] text-white/60">
          Pick a return date to complete the range.
        </div>
      )}
    </div>
  );
}
